import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

/**
 * Texas Professional License Renewal Landing Page
 */

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const TXLicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 12,
      cycleYears: 1,
      terminology: 'LU',
      highlights: ['12 LU annually', 'Min 1 LU in Health, Safety & Welfare', 'Max 8 LU self-study', 'Calendar year basis (Jan 1 - Dec 31)', 'Reportable by January 31'],
      stateBoard: 'Texas Board of Architectural Examiners',
      boardUrl: 'https://www.tbae.texas.gov/'
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 15,
      cycleYears: 1,
      terminology: 'PDH',
      highlights: ['15 PDH annually', '1 PDH in Ethics (mandatory)', 'Technical engineering subjects', 'Calendar year basis (Jan 1 - Dec 31)', 'Max 7.5 PDH self-study'],
      stateBoard: 'Texas Board of Professional Engineers & Land Surveyors',
      boardUrl: 'https://engineers.texas.gov/'
    },
    {
      id: 'PLS',
      name: 'Registered Professional Land Surveyor',
      icon: '📐',
      credits: 15,
      cycleYears: 1,
      terminology: 'PDH',
      highlights: ['15 PDH annually', '1 PDH in Ethics (mandatory)', 'Land surveying topics', 'Calendar year basis (Jan 1 - Dec 31)', 'Max 7.5 PDH self-study'],
      stateBoard: 'Texas Board of Professional Engineers & Land Surveyors',
      boardUrl: 'https://engineers.texas.gov/'
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 120,
      cycleYears: 3,
      terminology: 'CPE',
      highlights: ['120 CPE hours triennial', 'Min 20 hours annually', '4 hours Ethics (every 2 years)', 'Min 60% technical subjects', 'Triennial basis (3-year period)'],
      stateBoard: 'Texas State Board of Public Accountancy',
      boardUrl: 'https://www.tsbpa.texas.gov/'
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 20,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: ['20 contact hours biennial', 'Nursing practice-related', 'Max 10 hours self-study', 'Approved CE providers', 'First renewal exempt if licensed <4 years'],
      stateBoard: 'Texas Board of Nursing',
      boardUrl: 'https://www.bon.texas.gov/'
    },
    {
      id: 'APRN',
      name: 'Advanced Practice Registered Nurse',
      icon: '🩺',
      credits: 20,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: ['20 contact hours biennial', 'Advanced practice nursing topics', 'Max 10 hours self-study', 'Pharmacology recommended', 'Must maintain national certification'],
      stateBoard: 'Texas Board of Nursing',
      boardUrl: 'https://www.bon.texas.gov/'
    },
    {
      id: 'LCSW',
      name: 'Licensed Clinical Social Worker',
      icon: '🧠',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', '3 hours Professional Ethics (mandatory)', '6 hours Cultural Diversity', 'Clinical social work practice', 'Max 15 hours self-study'],
      stateBoard: 'Texas State Board of Social Worker Examiners',
      boardUrl: 'https://www.bhec.texas.gov/texas-state-board-of-social-worker-examiners/'
    },
    {
      id: 'LPC',
      name: 'Licensed Professional Counselor',
      icon: '💭',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', '3 hours Professional Ethics (mandatory)', '6 hours Cultural Diversity', 'Professional counseling topics', 'Max 15 hours self-study'],
      stateBoard: 'Texas State Board of Examiners of Professional Counselors',
      boardUrl: 'https://www.bhec.texas.gov/texas-state-board-of-examiners-of-professional-counselors/'
    },
    {
      id: 'LMFT',
      name: 'Licensed Marriage & Family Therapist',
      icon: '👨‍👩‍👧‍👦',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', '3 hours Professional Ethics (mandatory)', '6 hours Cultural Diversity', 'Marriage and family therapy topics', 'Max 15 hours self-study'],
      stateBoard: 'Texas State Board of Examiners of Marriage & Family Therapists',
      boardUrl: 'https://www.bhec.texas.gov/texas-state-board-of-examiners-of-marriage-and-family-therapists/'
    },
    {
      id: 'PSY',
      name: 'Licensed Psychologist',
      icon: '🧑‍⚕️',
      credits: 40,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['40 hours biennial', '3 hours Professional Ethics (mandatory)', '6 hours Cultural Diversity', 'Psychology practice topics', 'Max 20 hours self-study'],
      stateBoard: 'Texas State Board of Examiners of Psychologists',
      boardUrl: 'https://www.bhec.texas.gov/texas-state-board-of-examiners-of-psychologists/'
    },
    {
      id: 'RE',
      name: 'Real Estate Broker/Sales Agent',
      icon: '🏠',
      credits: 18,
      cycleYears: 2,
      terminology: 'SAE hours',
      highlights: ['18 SAE hours biennial', 'Sales agents and brokers', 'State-approved courses', 'Legal Update I & II (8 hours)', 'Completion required before renewal'],
      stateBoard: 'Texas Real Estate Commission',
      boardUrl: 'https://www.trec.texas.gov/'
    },
    {
      id: 'ATT',
      name: 'Attorney',
      icon: '⚖️',
      credits: 15,
      cycleYears: 1,
      terminology: 'MCLE',
      highlights: ['15 MCLE hours annually', '3 hours Legal Ethics (mandatory)', '1 hour Elimination of Bias', 'Accredited programs required', 'Max 7.5 hours self-study'],
      stateBoard: 'State Bar of Texas - MCLE',
      boardUrl: 'https://www.texasbar.com/mcle'
    }
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Texas Professional License Renewal Requirements",
      "description": "Complete guide to Texas professional license renewal requirements for Architects, Engineers, CPAs, Nurses, Social Workers, and more.",
      "url": "https://cectrack.com/tx-license-renewal",
      "about": {
        "@type": "Thing",
        "name": "Professional License Renewal",
        "sameAs": [
          "https://www.texas.gov/",
          "https://www.texasbar.com/"
        ]
      },
      "publisher": {
        "@type": "Organization",
        "name": "CEC Track",
        "url": "https://cectrack.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://cectrack.com/og-image.png"
        }
      },
      "mainEntity": {
        "@type": "ItemList",
        "name": "Texas Professional Licenses",
        "numberOfItems": 12,
        "itemListElement": professions.map((prof, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Course",
            "name": prof.name,
            "description": `${prof.name} license renewal requires ${prof.credits} ${prof.terminology} every ${prof.cycleYears} year(s)`,
            "provider": {
              "@type": "Organization",
              "name": prof.stateBoard,
              "url": prof.boardUrl
            }
          }
        }))
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://cectrack.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Texas License Renewal",
            "item": "https://cectrack.com/tx-license-renewal"
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How many CE hours do I need to renew my Texas professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CE requirements vary by profession in Texas. Architects need 12 LU annually, Engineers and Surveyors need 15 PDH annually, CPAs need 120 CPE hours triennial, RNs and APRNs need 20 contact hours biennial, Mental Health professionals need 30-40 hours biennial, Real Estate professionals need 18 SAE hours biennial, and Attorneys need 15 MCLE hours annually."
          }
        },
        {
          "@type": "Question",
          "name": "Which state agencies handle professional license renewal in Texas?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Texas has multiple licensing boards: Texas Board of Architectural Examiners, Texas Board of Professional Engineers & Land Surveyors (TBPELS), Texas State Board of Public Accountancy (TSBPA), Texas Board of Nursing (BON), Texas Behavioral Health Executive Council (BHEC) for mental health professionals, Texas Real Estate Commission (TREC), and State Bar of Texas for attorneys."
          }
        },
        {
          "@type": "Question",
          "name": "What are the mandatory CE topics for Texas professionals?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Texas has several mandatory CE requirements: Architects need 1 LU in HSW; Engineers and Surveyors need 1 PDH in Ethics; CPAs need 4 hours Ethics every 2 years; Mental Health professionals need 3 hours Professional Ethics and 6 hours Cultural Diversity; Real Estate professionals need 8 hours Legal Update; Attorneys need 3 hours Legal Ethics and 1 hour Elimination of Bias."
          }
        },
        {
          "@type": "Question",
          "name": "How often do I need to renew my Texas professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Texas renewal periods vary: Architects, Engineers, Surveyors, and Attorneys renew annually; most other professions including Nurses, Mental Health professionals, and Real Estate professionals renew biennially (every 2 years); CPAs renew every 3 years (triennially)."
          }
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "Texas Board of Professional Engineers and Land Surveyors",
      "alternateName": "TBPELS",
      "url": "https://engineers.texas.gov/",
      "telephone": "+1-512-440-7723",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "1917 IH 35 South",
        "addressLocality": "Austin",
        "addressRegion": "TX",
        "postalCode": "78741",
        "addressCountry": "US"
      },
      "areaServed": "Texas",
      "description": "Regulates Professional Engineers and Land Surveyors in Texas"
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "State Bar of Texas",
      "url": "https://www.texasbar.com/",
      "telephone": "+1-800-204-2222",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "1414 Colorado Street",
        "addressLocality": "Austin",
        "addressRegion": "TX",
        "postalCode": "78701",
        "addressCountry": "US"
      },
      "areaServed": "Texas",
      "description": "Administers Minimum Continuing Legal Education (MCLE) requirements for attorneys licensed in Texas"
    }
  ];

  return (
    <>
      <SkipToContent />
      <SEOHelmet
        title="Texas Professional License Renewal | CE Requirements & Deadlines"
        description="Complete guide to Texas professional license renewal requirements. Track CE credits for Architects, Engineers, CPAs, Nurses, Social Workers, Attorneys, and more."
        keywords="Texas license renewal, TX CE requirements, TX continuing education, Texas professional licenses, TBPELS, Texas nurses, TX CPAs, TX engineers, TX attorneys"
        canonical="https://cectrack.com/tx-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section 
        aria-label="Page header"
        style={{
          background: 'linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)',
          color: 'white',
          padding: '80px 0 60px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                Texas Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Never miss a deadline. Track your continuing education requirements across all your Texas professional licenses in one place.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link 
                  to="/register" 
                  className="btn btn-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                >
                  Start Tracking Free
                </Link>
                <a 
                  href="#professions" 
                  className="btn btn-outline-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px' }}
                >
                  View Requirements
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section aria-label="Statistics" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#b91c1c', marginBottom: '0.5rem' }} aria-label="12 professions">12</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#b91c1c', marginBottom: '0.5rem' }} aria-label="Over 55 categories">55+</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Category Requirements</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#b91c1c', marginBottom: '0.5rem' }} aria-label="100 percent compliant">100%</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>State Board Compliant</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#b91c1c', marginBottom: '0.5rem' }} aria-label="24/7 access">24/7</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Access Anywhere</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professions Grid */}
      <main id="main-content">
        <section id="professions" aria-labelledby="professions-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="professions-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
                Texas CE Requirements by Profession
              </h2>
              <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
                Select your profession to see detailed continuing education requirements
              </p>
            </div>

          <div className="row g-4">
            {professions.map((prof) => (
              <div key={prof.id} className="col-md-6 col-lg-4">
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ 
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                    <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>
                      {prof.name}
                    </h5>
                    
                    <div 
                      style={{
                        backgroundColor: '#fee2e2',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}
                    >
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#b91c1c' }}>
                        {prof.credits} {prof.terminology}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                        {prof.cycleYears === 1 ? 'Annual' : prof.cycleYears === 2 ? 'Biennial' : 'Triennial'}
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                        Key Requirements:
                      </div>
                      <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                        {prof.highlights.map((highlight, idx) => (
                          <li key={idx} style={{ marginBottom: '0.3rem' }}>{highlight}</li>
                        ))}
                      </ul>
                    </div>

                    <div 
                      style={{
                        paddingTop: '1rem',
                        borderTop: '1px solid #e9ecef',
                        fontSize: '0.85rem',
                        color: '#6c757d'
                      }}
                    >
                      <strong>{prof.stateBoard}</strong>
                      <br />
                      <a 
                        href={prof.boardUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#ef4444', textDecoration: 'none' }}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        Visit State Board →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose CEC Track */}
      <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
                Simplify Your Texas License Management
              </h2>
              <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>
                Managing multiple Texas professional licenses doesn't have to be complicated. 
                CEC Track automatically tracks your requirements, calculates your progress, and sends 
                timely reminders so you never miss a renewal deadline.
              </p>
              <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                {[
                  'Automated deadline tracking for all TX professions',
                  'Upload certificates and match to mandatory topics',
                  'Real-time progress reports by category',
                  'Multi-state support for professionals licensed in multiple states',
                  'Secure cloud storage for all documents',
                  'Email notifications for upcoming deadlines'
                ].map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#ef4444', fontSize: '1.5rem' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <div 
                className="p-5 text-center"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>
                  Start Tracking Today
                </h3>
                <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                  Join hundreds of Texas professionals who trust CEC Track to manage their continuing education.
                </p>
                <Link 
                  to="/register" 
                  className="btn btn-primary btn-lg px-5 py-3"
                  style={{ 
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)',
                    border: 'none',
                    borderRadius: '8px'
                  }}
                >
                  Create Free Account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* State Resources */}
      <section aria-labelledby="resources-heading" style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 id="resources-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              Texas State Resources
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              Official resources and contact information for Texas professional licensing
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    Texas Board of Professional Engineers & Land Surveyors
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Regulates Professional Engineers and Registered Professional Land Surveyors in Texas. Administers annual CE requirements including mandatory Ethics.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+15124407723" aria-label="Call TBPELS at 512-440-7723">(512) 440-7723</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://engineers.texas.gov/" target="_blank" rel="noopener noreferrer" aria-label="Visit TBPELS website">
                      engineers.texas.gov
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> 1917 IH 35 South, Austin, TX 78741
                  </div>
                  <a 
                    href="https://engineers.texas.gov/continuing-education" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="View CE requirements"
                  >
                    View CE Requirements
                  </a>
                </div>
              </article>
            </div>

            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    State Bar of Texas
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Administers Minimum Continuing Legal Education (MCLE) requirements for attorneys licensed in Texas. Mandatory annual MCLE includes Legal Ethics and Elimination of Bias.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+18002042222" aria-label="Call State Bar at 800-204-2222">(800) 204-2222</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://www.texasbar.com/" target="_blank" rel="noopener noreferrer" aria-label="Visit State Bar website">
                      www.texasbar.com
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> 1414 Colorado Street, Austin, TX 78701
                  </div>
                  <a 
                    href="https://www.texasbar.com/mcle" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="View MCLE requirements"
                  >
                    View MCLE Requirements
                  </a>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* Other States Cross-Linking */}
      <OtherStates currentState="TX" />

      {/* CTA Section */}
      <section 
        aria-label="Call to action"
        style={{
          background: 'linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)',
          color: 'white',
          padding: '80px 0'
        }}
      >
        <div className="container text-center">
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
            Ready to Simplify Your Texas License Renewal?
          </h2>
          <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>
            Join professionals across Texas who never worry about missing CE requirements
          </p>
          <Link 
            to="/register" 
            className="btn btn-light btn-lg px-5 py-3"
            style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)' }}
          >
            Get Started Free
          </Link>
        </div>
      </section>
      </main>
    </>
  );
};

export default TXLicenseRenewal;
